import BusinessIcon from '@mui/icons-material/Business';
import LogoutIcon from '@mui/icons-material/Logout';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from '@tanstack/react-location';
import { useState } from 'react';
import useCurrentUser from '../../../../core/hooks/useCurrentUser';

const UserDropdown = () => {
    const { user, loading, isGlobalAdmin } = useCurrentUser();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleNavigation = (path: string) => {
        navigate({ to: path, replace: false });
        handleClose();
    };

    const handleLogout = () => {
        localStorage.setItem('mushroom_logout', '1');
        localStorage.removeItem('token');
        // eslint-disable-next-line no-restricted-globals
        location.reload();
    };

    if (loading || !user) {
        return null;
    }

    const buttonContent = isSmallScreen ? (
        <IconButton
            onClick={handleClick}
            aria-controls={open ? 'user-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            aria-label={`User menu for ${user.name}`}
            sx={{
                border: '2px solid #6366f1',
                borderRadius: 0,
                backgroundColor: '#1a1a2e',
                color: '#6366f1',
                '&:hover': {
                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                },
            }}
        >
            <PersonIcon />
        </IconButton>
    ) : (
        <Box
            component="button"
            onClick={handleClick}
            aria-controls={open ? 'user-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            aria-label={`User menu for ${user.name}`}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                backgroundColor: '#1a1a2e',
                border: '2px solid #6366f1',
                borderRadius: 0,
                px: 2,
                py: 1,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                    transform: 'translateX(2px)',
                },
            }}
        >
            <PersonIcon
                sx={{
                    color: '#6366f1',
                    fontSize: '1.25rem',
                }}
            />
            <Typography
                sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontSize: '0.875rem',
                    color: '#fff',
                }}
            >
                {user.name}
            </Typography>
        </Box>
    );

    return (
        <>
            {buttonContent}
            <Menu
                id="user-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 0,
                            border: '2px solid #6366f1',
                            backgroundColor: '#1a1a2e',
                            mt: 0.5,
                            minWidth: 180,
                        },
                    },
                }}
            >
                <MenuItem
                    onClick={() => handleNavigation('/properties')}
                    sx={{
                        borderLeft: '4px solid transparent',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        fontSize: '0.875rem',
                        py: 1.5,
                        px: 2,
                        gap: 1.5,
                        '&:hover': {
                            borderLeftColor: '#6366f1',
                            backgroundColor: 'rgba(99, 102, 241, 0.1)',
                            transform: 'translateX(2px)',
                        },
                    }}
                >
                    <BusinessIcon
                        sx={{
                            color: '#6366f1',
                            fontSize: '1.25rem',
                        }}
                    />
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: '0.875rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                        }}
                    >
                        Properties
                    </Typography>
                </MenuItem>
                <MenuItem
                    onClick={() => handleNavigation('/settings')}
                    sx={{
                        borderLeft: '4px solid transparent',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        fontSize: '0.875rem',
                        py: 1.5,
                        px: 2,
                        gap: 1.5,
                        '&:hover': {
                            borderLeftColor: '#6366f1',
                            backgroundColor: 'rgba(99, 102, 241, 0.1)',
                            transform: 'translateX(2px)',
                        },
                    }}
                >
                    <SettingsIcon
                        sx={{
                            color: '#6366f1',
                            fontSize: '1.25rem',
                        }}
                    />
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: '0.875rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                        }}
                    >
                        Settings
                    </Typography>
                </MenuItem>
                {isGlobalAdmin() && (
                    <MenuItem
                        onClick={() => handleNavigation('/users')}
                        sx={{
                            borderLeft: '4px solid transparent',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            fontSize: '0.875rem',
                            py: 1.5,
                            px: 2,
                            gap: 1.5,
                            '&:hover': {
                                borderLeftColor: '#6366f1',
                                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                transform: 'translateX(2px)',
                            },
                        }}
                    >
                        <PeopleIcon
                            sx={{
                                color: '#6366f1',
                                fontSize: '1.25rem',
                            }}
                        />
                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                            }}
                        >
                            Users
                        </Typography>
                    </MenuItem>
                )}
                <Divider
                    sx={{
                        my: 0.5,
                        borderColor: '#374151',
                    }}
                />
                <MenuItem
                    onClick={handleLogout}
                    sx={{
                        borderLeft: '4px solid transparent',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        fontSize: '0.875rem',
                        py: 1.5,
                        px: 2,
                        gap: 1.5,
                        '&:hover': {
                            borderLeftColor: '#ef4444',
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            transform: 'translateX(2px)',
                        },
                    }}
                >
                    <LogoutIcon
                        sx={{
                            color: '#ef4444',
                            fontSize: '1.25rem',
                        }}
                    />
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: '0.875rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                        }}
                    >
                        Logout
                    </Typography>
                </MenuItem>
            </Menu>
        </>
    );
};

export default UserDropdown;
